/**
 * State + thunks for the Org Settings → Assignments tab.
 *
 * Talks to /api/org/:orgId/assignments/* (server enforces ASSIGN_CREATORS
 * for writes; this duck is just a transport).
 *
 * Spec: UPDATE.md §3.6.2 + §3.6.8
 */

import debounce from 'lodash/debounce';
import { assignmentsAPI, usersAPI, orgListingsAPI } from '../../api';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';
import { PAGE_TYPE_QUERY } from '../../util/listingType';

const SEARCH_DEBOUNCE_MS = 250;

// ================ Action types ================ //

export const FETCH_REQUEST = 'app/OrgSettingsAssignmentsPage/FETCH_REQUEST';
export const FETCH_SUCCESS = 'app/OrgSettingsAssignmentsPage/FETCH_SUCCESS';
export const FETCH_ERROR = 'app/OrgSettingsAssignmentsPage/FETCH_ERROR';

export const CREATE_REQUEST = 'app/OrgSettingsAssignmentsPage/CREATE_REQUEST';
export const CREATE_SUCCESS = 'app/OrgSettingsAssignmentsPage/CREATE_SUCCESS';
export const CREATE_ERROR = 'app/OrgSettingsAssignmentsPage/CREATE_ERROR';

export const REVOKE_REQUEST = 'app/OrgSettingsAssignmentsPage/REVOKE_REQUEST';
export const REVOKE_SUCCESS = 'app/OrgSettingsAssignmentsPage/REVOKE_SUCCESS';
export const REVOKE_ERROR = 'app/OrgSettingsAssignmentsPage/REVOKE_ERROR';

export const SEARCH_CREATORS_REQUEST = 'app/OrgSettingsAssignmentsPage/SEARCH_CREATORS_REQUEST';
export const SEARCH_CREATORS_SUCCESS = 'app/OrgSettingsAssignmentsPage/SEARCH_CREATORS_SUCCESS';
export const SEARCH_CREATORS_ERROR = 'app/OrgSettingsAssignmentsPage/SEARCH_CREATORS_ERROR';

export const FETCH_LIVE_ROOMS_REQUEST = 'app/OrgSettingsAssignmentsPage/FETCH_LIVE_ROOMS_REQUEST';
export const FETCH_LIVE_ROOMS_SUCCESS = 'app/OrgSettingsAssignmentsPage/FETCH_LIVE_ROOMS_SUCCESS';
export const FETCH_LIVE_ROOMS_ERROR = 'app/OrgSettingsAssignmentsPage/FETCH_LIVE_ROOMS_ERROR';

export const FETCH_SHOWS_REQUEST = 'app/OrgSettingsAssignmentsPage/FETCH_SHOWS_REQUEST';
export const FETCH_SHOWS_SUCCESS = 'app/OrgSettingsAssignmentsPage/FETCH_SHOWS_SUCCESS';
export const FETCH_SHOWS_ERROR = 'app/OrgSettingsAssignmentsPage/FETCH_SHOWS_ERROR';

// ================ Reducer ================ //

const initialState = {
  orgId: null,
  assignments: [],
  // creatorId → { id, displayName, avatarUrl } map, resolved server-side
  // when the assignment list loads. Used by AssignmentRow to render
  // the Creator column.
  creators: {},

  fetchInProgress: false,
  fetchLoaded: false,
  fetchError: null,

  createInProgress: false,
  createError: null,

  revokingId: null,
  revokeError: null,

  // Form-helper slices — used only by the AssignCreatorForm modal
  creatorOptions: [],
  searchCreatorsInProgress: false,
  searchCreatorsError: null,

  liveRoomOptions: [],
  liveRoomsLoadingFor: null, // creatorId being loaded, for in-form spinner
  liveRoomsError: null,

  showOptions: [],
  showsLoading: false,
  showsError: null,
};

const upsert = (list, record) => {
  if (!record?.id) return list;
  const idx = list.findIndex(a => a?.id === record.id);
  if (idx < 0) return [...list, record];
  const next = [...list];
  next[idx] = record;
  return next;
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_REQUEST:
      // On an org switch (different :orgId, same mounted component) drop the
      // previous org's assignments so they can't flash under the new org
      // during its fetch. Same orgId → keep them (stale-while-revalidate).
      return payload && payload.orgId !== state.orgId
        ? { ...initialState, orgId: payload.orgId, fetchInProgress: true }
        : { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fetchLoaded: true,
        orgId: payload.orgId,
        assignments: payload.assignments || [],
        creators: payload.creators || {},
      };
    case FETCH_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case CREATE_REQUEST:
      return { ...state, createInProgress: true, createError: null };
    case CREATE_SUCCESS:
      return {
        ...state,
        createInProgress: false,
        assignments: upsert(state.assignments, payload),
      };
    case CREATE_ERROR:
      return { ...state, createInProgress: false, createError: payload };

    case REVOKE_REQUEST:
      return { ...state, revokingId: payload, revokeError: null };
    case REVOKE_SUCCESS:
      return {
        ...state,
        revokingId: null,
        assignments: upsert(state.assignments, payload),
      };
    case REVOKE_ERROR:
      return { ...state, revokingId: null, revokeError: payload };

    case SEARCH_CREATORS_REQUEST:
      return { ...state, searchCreatorsInProgress: true, searchCreatorsError: null };
    case SEARCH_CREATORS_SUCCESS:
      return {
        ...state,
        searchCreatorsInProgress: false,
        creatorOptions: payload || [],
      };
    case SEARCH_CREATORS_ERROR:
      return { ...state, searchCreatorsInProgress: false, searchCreatorsError: payload };

    case FETCH_LIVE_ROOMS_REQUEST:
      return { ...state, liveRoomsLoadingFor: payload, liveRoomsError: null };
    case FETCH_LIVE_ROOMS_SUCCESS:
      return { ...state, liveRoomsLoadingFor: null, liveRoomOptions: payload || [] };
    case FETCH_LIVE_ROOMS_ERROR:
      return { ...state, liveRoomsLoadingFor: null, liveRoomsError: payload };

    case FETCH_SHOWS_REQUEST:
      return { ...state, showsLoading: true, showsError: null };
    case FETCH_SHOWS_SUCCESS:
      return { ...state, showsLoading: false, showOptions: payload || [] };
    case FETCH_SHOWS_ERROR:
      return { ...state, showsLoading: false, showsError: payload };

    default:
      return state;
  }
}

// ================ Thunks ================ //

const unwrap = res => {
  const body = res?.data ?? res ?? {};
  return body?.data ?? body;
};

export const fetchOrgAssignments = orgId => async dispatch => {
  dispatch({ type: FETCH_REQUEST, payload: { orgId } });
  try {
    const res = await assignmentsAPI.listForOrg(orgId);
    const { assignments, creators } = unwrap(res);
    dispatch({
      type: FETCH_SUCCESS,
      payload: {
        orgId,
        assignments: assignments || [],
        creators: creators || {},
      },
    });
    return assignments;
  } catch (e) {
    dispatch({ type: FETCH_ERROR, payload: storableError(e) });
    throw e;
  }
};

export const fetchOrgShows = orgId => async dispatch => {
  dispatch({ type: FETCH_SHOWS_REQUEST });
  try {
    // pub_type (not pub_listingType) is the field the rest of the app
    // filters by — see config.listingTypes and OrgShowsPage.duck.
    const res = await orgListingsAPI.query(orgId, { pub_type: PAGE_TYPE_QUERY });
    // orgListingsAPI.query returns the deserialised SDK body
    // ({ data: [...listings], meta }) — the listings live at .data.
    const listings = Array.isArray(res?.data) ? res.data : [];
    const options = listings.map(l => ({
      id: l?.id?.uuid || l?.id,
      title: l?.attributes?.title || '(untitled)',
    }));
    dispatch({ type: FETCH_SHOWS_SUCCESS, payload: options });
    return options;
  } catch (e) {
    dispatch({ type: FETCH_SHOWS_ERROR, payload: storableError(e) });
    return [];
  }
};

// Map a Sharetribe user object to the form's `{ id, displayName, email }`
// option shape. The server returns the SDK envelope verbatim (see
// formatUsersResponse), so the array lives at `body.data.data`.
const usersResponseToOptions = res => {
  const body = res?.data ?? {};
  // Newer responses: { data: { data: [...users] } }. Older fallback:
  // { data: [...users] } in case anyone shape-shifts this later.
  const arr = Array.isArray(body?.data?.data)
    ? body.data.data
    : Array.isArray(body?.data)
    ? body.data
    : [];
  return arr
    .map(u => {
      const profile = u?.attributes?.profile || {};
      const displayName =
        profile.displayName ||
        [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
        profile.publicData?.name?.displayName ||
        'Unnamed';
      return {
        id: u?.id?.uuid || u?.id,
        displayName,
        email: u?.attributes?.email || null,
        // Keep the raw Sharetribe user object so result rows can render
        // <Avatar user={opt.user} /> exactly the way every other surface
        // does (profileImage variant, initials fallback, banned-user
        // handling, …).
        user: u,
      };
    })
    .filter(u => u.id);
};

// Network call — debounced so we don't hit the server on every
// keystroke. The REQUEST action is dispatched synchronously in
// `searchCreators` so the input spinner appears immediately; the
// network call itself waits for the debounce to settle.
const runDebouncedSearch = debounce(async (dispatch, searchTerm) => {
  try {
    const res = await usersAPI.search({ searchTerm, limit: 10 });
    dispatch({ type: SEARCH_CREATORS_SUCCESS, payload: usersResponseToOptions(res) });
  } catch (e) {
    dispatch({ type: SEARCH_CREATORS_ERROR, payload: storableError(e) });
  }
}, SEARCH_DEBOUNCE_MS);

export const searchCreators = (searchTerm = '') => dispatch => {
  const term = String(searchTerm).trim();
  if (!term) {
    // Empty query: cancel any pending search and clear the list
    // immediately so the UI doesn't show stale matches.
    runDebouncedSearch.cancel();
    dispatch({ type: SEARCH_CREATORS_SUCCESS, payload: [] });
    return;
  }
  dispatch({ type: SEARCH_CREATORS_REQUEST });
  runDebouncedSearch(dispatch, term);
};

export const fetchCreatorLiveRooms = (orgId, creatorId) => async dispatch => {
  dispatch({ type: FETCH_LIVE_ROOMS_REQUEST, payload: creatorId });
  try {
    const res = await assignmentsAPI.listCreatorLiveRooms(orgId, creatorId);
    const { liveRooms } = unwrap(res);
    dispatch({ type: FETCH_LIVE_ROOMS_SUCCESS, payload: liveRooms || [] });
    return liveRooms || [];
  } catch (e) {
    dispatch({ type: FETCH_LIVE_ROOMS_ERROR, payload: storableError(e) });
    return [];
  }
};

export const createAssignment = (orgId, body) => async dispatch => {
  dispatch({ type: CREATE_REQUEST });
  try {
    const res = await assignmentsAPI.createForOrg(orgId, body);
    const { assignment } = unwrap(res);
    dispatch({ type: CREATE_SUCCESS, payload: assignment });
    return assignment;
  } catch (e) {
    dispatch({ type: CREATE_ERROR, payload: storableError(e) });
    throw e;
  }
};

export const revokeAssignment = (orgId, assignmentId) => async dispatch => {
  dispatch({ type: REVOKE_REQUEST, payload: assignmentId });
  try {
    const res = await assignmentsAPI.revokeForOrg(orgId, assignmentId);
    const { assignment } = unwrap(res);
    dispatch({ type: REVOKE_SUCCESS, payload: assignment });
    return assignment;
  } catch (e) {
    dispatch({ type: REVOKE_ERROR, payload: storableError(e) });
    throw e;
  }
};

// ================ loadData ================ //

export const loadData = params => async dispatch => {
  const { orgId } = params || {};
  if (!orgId) return null;

  // currentUser + org profiles must be in store for the org-context routing
  // guards in OrgSettingsPage to succeed (same as the Teams tab).
  await Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(fetchOrganizationProfiles()).catch(() => null),
  ]);

  await Promise.all([
    dispatch(fetchOrgAssignments(orgId)).catch(() => null),
    dispatch(fetchOrgShows(orgId)).catch(() => null),
  ]);
  return null;
};
